/**
 * Shared sub-components for the Instructions modal and the in-tutorial Reference Rail.
 *
 * The two surfaces present the same content (sections about pieces, captures, victory
 * conditions, progressions, strategies) but through different shells: the modal is a
 * slide-in drawer with CTAs; the rail is a static layout column docked to the left of the
 * tutorial board. Extracting these primitives here lets both consumers stay thin.
 */

import type { Strategy } from "./strategies";
import { theme, FONT_RUNE } from "./theme";

export { ProgressionDiagram } from "./ProgressionDiagram";

/**
 * Collapsible details/summary section. Supports both controlled (parent owns `open`/`onToggle`)
 * and uncontrolled (`defaultOpen`) modes — the Instructions modal uses controlled mode to enforce
 * accordion behavior; the rail uses uncontrolled mode for independent expand/collapse per section.
 */
export function Section({
  id,
  title,
  defaultOpen,
  open,
  onToggle,
  children,
}: {
  id?: string;
  title: string;
  defaultOpen?: boolean;
  open?: boolean;
  onToggle?: () => void;
  children: React.ReactNode;
}) {
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : undefined;
  return (
    <details
      id={id}
      open={isControlled ? isOpen : defaultOpen}
      style={{ borderBottom: `1px solid ${theme.boardEdge}`, padding: "12px 0" }}
    >
      <summary
        onClick={
          isControlled
            ? (e) => {
                e.preventDefault();
                onToggle?.();
              }
            : undefined
        }
        className="rune"
        style={{
          color: theme.accentSoft,
          fontSize: 13,
          letterSpacing: 2,
          padding: "4px 0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span>{title.toUpperCase()}</span>
        <span
          aria-hidden
          style={{
            fontSize: 14,
            opacity: 0.7,
            transform: (isControlled ? isOpen : defaultOpen) ? "rotate(0deg)" : "rotate(-90deg)",
            transition: "transform 150ms ease",
          }}
        >
          ▾
        </span>
      </summary>
      <div style={{ paddingTop: 8 }}>{children}</div>
    </details>
  );
}

export function PieceRow({
  shape,
  name,
  values,
  motion,
}: {
  shape: string;
  name: string;
  values: string;
  motion: string;
}) {
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "8px 0" }}>
      <div
        className="rune"
        style={{
          width: 32,
          textAlign: "center",
          color: theme.accent,
          fontSize: 22,
          paddingTop: 2,
        }}
      >
        {shape}
      </div>
      <div style={{ flex: 1 }}>
        <div className="rune" style={{ color: theme.parchment, fontSize: 14, marginBottom: 2 }}>
          {name}
          <span style={{ color: theme.boardLabel, fontSize: 11, marginLeft: 8, fontFamily: FONT_RUNE }}>
            {values}
          </span>
        </div>
        <div style={{ color: theme.parchment, opacity: 0.85, fontSize: 14, lineHeight: 1.5 }}>{motion}</div>
      </div>
    </div>
  );
}

export function CaptureRow({ name, detail, example }: { name: string; detail: string; example?: string }) {
  return (
    <div style={{ padding: "10px 0", borderBottom: `1px dashed ${theme.boardEdge}` }}>
      <div className="rune" style={{ color: theme.accent, fontSize: 14, marginBottom: 4 }}>
        {name}
      </div>
      <div style={{ color: theme.parchment, fontSize: 14, lineHeight: 1.5, opacity: 0.9 }}>{detail}</div>
      {example && (
        <div style={{ color: theme.boardLabel, fontSize: 13, marginTop: 4, fontStyle: "italic" }}>{example}</div>
      )}
    </div>
  );
}

export function VictoryRow({
  level,
  detail,
  cta,
}: {
  level: string;
  detail: string;
  cta?: { label: string; onClick: () => void };
}) {
  return (
    <div style={{ padding: "8px 0" }}>
      <div className="rune" style={{ color: theme.accent, fontSize: 14, marginBottom: 2 }}>
        {level}
      </div>
      <div style={{ color: theme.parchment, fontSize: 14, lineHeight: 1.5, opacity: 0.9 }}>{detail}</div>
      {cta && (
        <button
          onClick={cta.onClick}
          style={{
            marginTop: 4,
            padding: 0,
            background: "transparent",
            border: "none",
            color: theme.accentSoft,
            fontFamily: FONT_RUNE,
            fontSize: 12,
            letterSpacing: 1,
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          {cta.label}
        </button>
      )}
    </div>
  );
}

export function StrategyList({ items }: { items: Strategy[] }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
      {items.map((s, i) => (
        <details
          key={i}
          style={{
            background: theme.bg,
            border: `1px solid ${theme.boardEdge}`,
            borderRadius: 6,
            padding: "10px 12px",
          }}
        >
          <summary
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              cursor: "pointer",
            }}
          >
            <span style={{ flex: 1 }}>
              <span className="rune" style={{ color: theme.parchment, fontSize: 13 }}>
                {s.title}
              </span>
            </span>
            <DifficultyBadge difficulty={s.difficulty} />
          </summary>
          <div style={{ color: theme.parchment, fontSize: 14, lineHeight: 1.55, opacity: 0.9, marginTop: 8 }}>
            {s.body}
          </div>
        </details>
      ))}
    </div>
  );
}

export function DifficultyBadge({ difficulty }: { difficulty: Strategy["difficulty"] }) {
  const color =
    difficulty === "starter"
      ? theme.legalMove
      : difficulty === "intermediate"
        ? theme.accentSoft
        : theme.captureFlash;
  return (
    <span
      className="rune"
      style={{
        fontSize: 10,
        letterSpacing: 1,
        color,
        border: `1px solid ${color}`,
        borderRadius: 999,
        padding: "2px 8px",
        textTransform: "uppercase",
      }}
    >
      {difficulty}
    </span>
  );
}

